import { message } from "antd";
import Axios, { AxiosError, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import { Moment } from "moment";

import { FetchInvoiceQuery } from "hooks/api/billingEngine/invoices";
import { FetchMultiInvoiceQuery } from "hooks/api/billingEngine/useMultiOrganizationInvoiceReporting";

import { ImportedCSVQuery } from "interfaces/shippingCost/importedCSV";
import Cookies from "lib/Cookies";

import BillingEngineServiceError from "./BillingEngineError";
import {
  BillingEngineErrorResponse,
  BillingEngineServiceConfigs,
  BillingFrequencyEntity,
  BillingFrequencyResponse,
  BillingFrequencyUpdateRequestParams,
  Carrier,
  City,
  GetCitiesResponse,
  GetRateCardDetailsResponse,
  Organization,
  OrganizationDetailsResponse,
  RateCard,
  Service,
  BillingEngineSuccessMessageResponse,
  BillingEngineResponse,
} from "./interfaces";
import {
  GetVersionRequestParams,
  RateCardNewVersionRequestParams,
  RateCardZone,
  RateCardZonesUpdateRequestParams,
  RateCardZoneVersion,
  Supply,
  ShippingCost,
  GetCarriersResponse,
  ConsumableMaterial,
} from "./interfaces/entities";
import { Invoice } from "./interfaces/entities/Invoice";
import { WarehouseLoggedHoursType, WarehouseLoggedHours } from "./interfaces/entities/WarehouseLoggedHours";
import { ManualStockEntryUpdateRequest } from "./interfaces/requests/ManualStockEntry";
import { ShippingCostSaveRequest, ShippingCostUploadRequest } from "./interfaces/requests/ShippingCost";
import {
  WarehouseLoggedHoursUpdateRequest,
  WarehouseLoggedHoursDeleteRequest,
  WarehouseLoggedHoursCreateRequest,
} from "./interfaces/requests/WarehouseLoggedHours";
import {
  ShippingCostPaginatedResponse,
  ShippingCostResponse,
  ShippingCostErrorResponse,
} from "./interfaces/responses/ShippingCost";
import {
  WarehouseLoggedHoursGetResponse,
  WarehouseLoggedHoursDeleteResponse,
  WarehouseLoggedHoursCreateResponse,
} from "./interfaces/responses/WarehouseLoggedHours";

/**
 * Abstraction class for all requests to the Billing Engine Service
 */
class BillingEngineService {
  private fetch: AxiosInstance;
  private notifications: any;

  constructor(options: BillingEngineServiceConfigs) {
    options = options || {};

    this.fetch = options.fetch;
    this.notifications = options.notifications;
    //the shipping-cost file upload takes a bit of time; hence we need more than 1m
    this.fetch.defaults.timeout = 5 * 60000;

    // Attach interceptors
    this.fetch.interceptors.request.use(this.requestInterceptions);
  }

  /**
   * Transforms error to the same format
   * @param error
   */
  private transformError = (error: AxiosError<BillingEngineErrorResponse>): BillingEngineServiceError => {
    if (error.response) {
      const { data, statusCode } = error.response.data;

      if (data) {
        return new BillingEngineServiceError(data, statusCode);
      } else {
        return new BillingEngineServiceError(
          {
            error: "Server Error",
            message: "Oops, looks like something went wrong on the server.",
          },
          statusCode
        );
      }
    }

    return new BillingEngineServiceError({
      error: "Unexpected error.",
      message: error.message,
    });
  };

  /**
   * Dispatches notification and returns normalized error
   * @param error Error provided by fetch
   * @param silently Flag that determines if error should be propagated
   */
  private handleError = (
    error: AxiosError<BillingEngineErrorResponse>,
    silently = false
  ): BillingEngineServiceError => {
    const responseError = this.transformError(error);
    if (!silently) {
      this.notifications.error(responseError.message);
    }

    return responseError;
  };

  private requestInterceptions = (config: InternalAxiosRequestConfig) => {
    const token = Cookies.getUserToken();
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  };

  /**
   * Fetch organization details should include rate cards
   * for this organizations
   * @param organizationId Organization id
   */
  getOrganizationDetails = (key: string, organizationId: string): Promise<Organization> => {
    return this.fetch
      .get<OrganizationDetailsResponse>(`/organizations/${organizationId}`)
      .then((response) => response.data.data)
      .catch((error) => {
        throw this.handleError(error, true);
      });
  };

  getOrganizations = (key: string, search = "") => {
    return this.fetch
      .get(`/organizations`, {
        params: {
          search,
        },
      })
      .then((response) => response.data.data);
  };

  getWROs = (key: string, search = "") => {
    return this.fetch
      .get(`/fulfillment/wros`, {
        params: {
          search,
        },
      })
      .then((response) => {
        return response.data.data;
      });
  };

  /**
   * Add rate card to service
   * @param params Request parameters
   */
  addRateCardToService = (params: { serviceId: string; organizationId: string }): Promise<RateCard> => {
    const { organizationId, serviceId } = params;
    return this.fetch
      .post<GetRateCardDetailsResponse>(`/organizations/${organizationId}/rate-cards`, {
        serviceId,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        throw this.handleError(error);
      });
  };

  /**
   * Fetch rate card details should include zones that are combinations
   * of city information and last version
   * @param rateCardId Rate Card id
   */
  getRateCardDetails = (key: string, rateCardId: string): Promise<RateCard> => {
    return this.fetch
      .get<GetRateCardDetailsResponse>(`/rate-cards/${rateCardId}`)
      .then((response) => response.data.data)
      .catch((error) => {
        throw this.handleError(error);
      });
  };

  /**
   * Fetch all available cities
   */
  getCities = (): Promise<City[]> => {
    return this.fetch
      .get<GetCitiesResponse>("/cities", {
        params: {
          order: "desc",
          orderBy: "name",
        },
      })
      .then((response) => response.data.data)
      .catch((error) => {
        throw this.handleError(error);
      });
  };

  getConsumableMaterials = async (organizationId: string): Promise<ConsumableMaterial[]> => {
    return this.fetch
      .get<ConsumableMaterial[]>(`/supplies/organizations/${organizationId}`)
      .then((response) => response.data)
      .catch((error) => {
        throw this.handleError(error);
      });
  };

  /**
   * Fetch all logistics shipping carriers
   */
  getCarriers = (): Promise<Carrier[]> => {
    return this.fetch
      .get<GetCarriersResponse>("/logistics-shipping-carriers")
      .then((response) => response.data.data)
      .catch((error) => {
        throw this.handleError(error);
      });
  };

  /**
   * Get version for particular rate card zone
   * @param key Cache key
   * @param params Params
   */
  getRateCardZoneVersion = (key: string, params: GetVersionRequestParams): Promise<RateCardZoneVersion> => {
    const { rateCardId, rateCardZoneId, versionId } = params;

    return this.fetch
      .get(`/rate-cards/${rateCardId}/zones/${rateCardZoneId}/versions/${versionId}`)
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        throw this.handleError(error);
      });
  };

  /**
   * Create new version
   * @param rateCardId Rate Card Id
   * @param rateCardZoneId Rate Card Zone Id
   * @param version Version data
   * @TODO: orest - update version data
   */
  updateZoneRateCardVersion = (params: RateCardNewVersionRequestParams): Promise<RateCardZoneVersion> => {
    const { rateCardId, rateCardZoneId, version } = params;
    return this.fetch
      .post(`/rate-cards/${rateCardId}/zones/${rateCardZoneId}/versions`, {
        data: version,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        throw this.handleError(error);
      });
  };

  /**
   * Bulk updated of rate card zones
   * @param params Rate card zones
   */
  updateRateCardZones = (params: RateCardZonesUpdateRequestParams): Promise<RateCardZone[]> => {
    const { rateCardId, zones } = params;
    return this.fetch
      .put(`/rate-cards/${rateCardId}/zones/bulk`, zones)
      .then((response) => response.data.data)
      .catch((error) => {
        throw this.handleError(error);
      });
  };

  /**
   * Get Billing frequency for an organization
   * @param params Organization ID
   */
  getBillingFrequency = (key: string, organizationID: string): Promise<BillingFrequencyEntity> => {
    return this.fetch
      .get<BillingFrequencyResponse>(`/organizations/${organizationID}/billing-frequency`)
      .then((response) => response.data.data)
      .catch((error: AxiosError<BillingEngineErrorResponse>) => {
        throw this.handleError(error, true);
      });
  };

  /**
   * Creates Billing Frequency for an Organization
   * @param params Organization ID
   * @param params Billing Frequency
   */
  createBillingFrequency = (params: BillingFrequencyUpdateRequestParams): Promise<BillingFrequencyEntity> => {
    const { organizationID, billingFrequency } = params;

    return this.fetch
      .post<BillingFrequencyResponse>(`/organizations/${organizationID}/billing-frequency`, billingFrequency)
      .then((response) => response.data.data)
      .catch((error) => {
        throw this.handleError(error);
      });
  };

  /**
   * Updates Billing Frequency for an Organization
   * @param params Organization ID
   * @param params Billing Frequency
   */
  updateBillingFrequency = (params: BillingFrequencyUpdateRequestParams): Promise<BillingFrequencyEntity> => {
    const { organizationID, billingFrequency } = params;

    return this.fetch
      .put<BillingFrequencyResponse>(`/organizations/${organizationID}/billing-frequency`, billingFrequency)
      .then((response) => response.data.data)
      .catch((error) => {
        throw this.handleError(error);
      });
  };

  getWarehouseHours = (
    key: string,
    type: WarehouseLoggedHoursType,
    page: number,
    limit: number,
    query: any = {},
    order = "DESC"
  ): Promise<WarehouseLoggedHoursGetResponse> => {
    const params: any = {
      page,
      limit,
      order,
      ...query,
    };

    return this.fetch
      .get(`/fulfillment/log-hours/${type}`, {
        params,
      })
      .then((response) => response.data);
  };

  getSuppliesForOrganization = (key: string, organizationId: string): Promise<Supply[]> => {
    return this.fetch
      .get(`/organizations/${organizationId}/warehouse/supplies`)
      .then((response) => response.data.data)
      .catch((error) => {
        throw this.handleError(error);
      });
  };

  createWarehouseHours = ({ record, type }: WarehouseLoggedHoursCreateRequest): Promise<WarehouseLoggedHours> => {
    return this.fetch
      .post<WarehouseLoggedHoursCreateResponse>(`/fulfillment/log-hours/${type}`, record)
      .then((response) => response.data.data)
      .catch((error) => {
        throw this.handleError(error);
      });
  };

  updateWarehouseHours = ({ record, type }: WarehouseLoggedHoursUpdateRequest): Promise<WarehouseLoggedHours> => {
    return this.fetch
      .put(`/fulfillment/log-hours/${type}/${record.id}`, record)
      .then((response) => response.data.data)
      .catch((error) => {
        throw this.handleError(error);
      });
  };

  deleteWarehouseHours = ({ record, type }: WarehouseLoggedHoursDeleteRequest): Promise<any> => {
    return this.fetch
      .delete<WarehouseLoggedHoursDeleteResponse>(`/fulfillment/log-hours/${type}/${record.id}`)
      .then((response) => response.data.data)
      .catch((error) => {
        throw this.handleError(error);
      });
  };

  /**
   * Uploads and validates a shipping cost .csv file
   *
   * @param formData - formData with 'file' property containing a .csv file
   */
  uploadShippingCostsCSV = ({ formData }: ShippingCostUploadRequest): Promise<ShippingCost[]> => {
    return this.fetch
      .post<ShippingCostResponse>(`/logistics/shipping-costs/upload`, formData, {
        headers: {
          // eslint-disable-next-line
          ["Content-Type"]: "multipart/form-data",
        },
        timeout: 5 * 60 * 1000, // 5 minutes
      })
      .then((response) => response.data.data)
      .catch((error: AxiosError<ShippingCostErrorResponse>) => {
        // Handle validation errors separately
        // These are different from the typical validation errors found in other components
        if (error.response?.status === 422) {
          throw error.response.data;
        }

        throw this.handleError(error as any);
      });
  };

  saveShippingCostsCSV = ({ rows, carrierId }: ShippingCostSaveRequest): Promise<{ message: string }> => {
    return this.fetch
      .post<BillingEngineSuccessMessageResponse>(`/logistics/shipping-costs/save`, {
        data: rows.map((r) => {
          const { id, ...rest } = r;
          // remove id from body and let the backend generate uuids
          return rest;
        }),
        carrierId,
      })
      .then((response) => response.data.data);
  };

  getUploadedCSVs = (query: ImportedCSVQuery): Promise<ShippingCostPaginatedResponse> => {
    return this.fetch
      .get("/logistics/shipping-costs/", {
        params: query,
      })
      .then((response) => response.data);
  };

  getStockEntries = (key: string, params: any) => {
    return this.fetch.get(`/fulfillment/stock-entry`, { params }).then((response) => response.data);
  };

  updateStockEntries = (entries: ManualStockEntryUpdateRequest) => {
    return this.fetch
      .post(`/fulfillment/stock-entry`, entries)
      .then((response) => response.data.data)
      .catch((error) => {
        // Handle validation errors separately
        // These are different from the typical validation errors found in other components
        if (error.response?.status === 422) {
          throw error.response.data;
        }

        throw this.handleError(error);
      });
  };

  /**
   * Get invoices of an organization
   * @param params Params
   */
  getOrganizationInvoices = (params: FetchInvoiceQuery): Promise<Invoice[]> => {
    return this.fetch
      .get(`/organizations/${params.organizationId}/invoices`, { params })
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        throw this.handleError(error);
      });
  };

  /**
   * Get invoices for multiple organizations
   * @param params Params
   */
  getMultiOrganizationInvoices = (params: FetchMultiInvoiceQuery): Promise<Invoice[]> => {
    return this.fetch
      .get(`/invoices/reports`, { params })
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        throw this.handleError(error);
      });
  };

  /**
   * Create invoice reporting
   * @param params Request parameters
   */
  createInvoiceReporting = (params: {
    startDate: string;
    endDate: string;
    organizationId: string;
  }): Promise<RateCard> => {
    const { organizationId, startDate, endDate } = params;
    return this.fetch
      .post<GetRateCardDetailsResponse>(`/organizations/${organizationId}/invoices/reporting`, {
        startDate,
        endDate,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        throw this.handleError(error);
      });
  };

  /**
   * Create multiple invoice reporting
   * @param params Request parameters
   */
  createMultiOrganizationInvoiceReporting = (params: {
    startDate: Moment;
    endDate: Moment;
    name: string;
    organizationIds: string[];
    excludedOrganizationIds: string[];
  }): Promise<RateCard> => {
    const { startDate, endDate, name, organizationIds, excludedOrganizationIds } = params;

    return this.fetch
      .post<GetRateCardDetailsResponse>(`/invoices/reporting`, {
        startDate: startDate.toISOString(true), // Passing keepOffset as true ensures time isn't converted to UTC
        endDate: endDate.toISOString(true), // Passing keepOffset as true ensures time isn't converted to UTC
        name,
        organizationIds,
        excludedOrganizationIds,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        throw this.handleError(error);
      });
  };

  /**
   * Delete invoice
   * @param params Request parameters
   */
  deleteInvoice = (params: { invoiceId: string }): Promise<void> => {
    const { invoiceId } = params;
    return this.fetch
      .delete<BillingEngineResponse<void>>(`/invoices/${invoiceId}`)
      .then((response) => response.data.data)
      .catch((error) => {
        throw this.handleError(error);
      });
  };

  /**
   * Get NS Setting info for an organization
   */
  getOrganizationNsSettingsInfo = async (organizationId: string): Promise<{ netSuiteId: string }> => {
    return this.fetch
      .get(`/organizations/${organizationId}/organizationInfo`)
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        throw this.handleError(error);
      });
  };

  /**
   * Add NS Setting info for an organization
   */
  addOrganizationNsSettingsInfo = async ({
    organizationId,
    netSuiteId,
  }: {
    organizationId: string;
    netSuiteId: string;
  }) => {
    return this.fetch
      .post(`/organizations/${organizationId}/organizationInfo`, {
        netSuiteId,
      })
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        throw this.handleError(error);
      });
  };
}

const axiosInstance = Axios.create({
  baseURL: process.env.REACT_APP_BILLING_ENGINE_URL,
  timeout: 10000,
  headers: { "Content-Type": "application/json" },
  // withCredentials: true,
});

const instance = new BillingEngineService({
  fetch: axiosInstance,
  notifications: message,
});

export default Object.freeze(instance);
